import { forwardRef, useEffect, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import _ from 'lodash';
import clsx from 'clsx';
import WdSvgIcon from '../../core/WdSvgIcon';

/**
 * ButtonSelector
 *
 * Componente de botón desplegable para seleccionar una opción de una lista.
 *
 * @component
 * @param {Array<{ value: string|number, title: string, icon?: React.ReactNode, textColor?: string, bgColor?: string }>} data - Lista de opciones a mostrar
 * @param {number|string} [defaultOption=0] - Índice o valor por defecto de la opción seleccionada
 * @param {boolean} [disable] - Si el botón está deshabilitado
 * @param {function} [onChange] - Callback al cambiar la opción seleccionada (retorna el value)
 * @param {string} [className] - Clase CSS adicional para el botón
 * @param {React.Ref} ref - Referencia para el botón
 * @returns {JSX.Element}
 */
export const ButtonSelector = forwardRef((props, ref) => {
  const { data, defaultOption = 0, disable, onChange, className } = props;

  const [listData, setListData] = useState([]);
  const [selectedOption, setSelectedOption] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (optionValue, event) => {
    const getObjSelectedOpt = _.find(listData, { value: optionValue });
    setSelectedOption(getObjSelectedOpt);
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (selectedOption === undefined || typeof onChange !== 'function') return;
    onChange(selectedOption?.value);
  }, [selectedOption]);

  useEffect(() => {
    if (data === undefined || data === null || typeof data !== 'object') return;
    setListData(data);
  }, [data]);

  useEffect(() => {
    if (defaultOption === undefined) return;
    if (typeof defaultOption === 'number') {
      setSelectedOption(data[defaultOption]);
    } else if (typeof defaultOption === 'string') {
      const getObjSelectedOpt = _.find(data, { value: defaultOption });
      setSelectedOption(getObjSelectedOpt);
    }
  }, [data]);

  return (
    <>
      <Button
        ref={ref}
        id="priority-button"
        aria-controls="priority-menu"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className={clsx('min-w-96 px-12', className)}
        disable={disable}
        sx={{
          color: selectedOption?.textColor || undefined,
          backgroundColor: selectedOption?.bgColor || undefined,
        }}
      >
        <WdSvgIcon className="mx-4" size={16}>
          {selectedOption?.icon}
        </WdSvgIcon>
        <span>{selectedOption.title || undefined}</span>
      </Button>
      <Menu
        id="priority-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'priority-button',
        }}
      >
        {listData.map((item) => (
          <MenuItem onClick={(ev) => handleSelect(item.value, ev)} key={item.value}>
            <ListItemText primary={item.title} />
            <ListItemIcon
              className="min-w-40 justify-end"
              sx={{ color: item.textColor || undefined }}
            >
              <WdSvgIcon size={16}>{item.icon}</WdSvgIcon>
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});
